import React from 'react';
import Papa from 'papaparse';
import delay from 'lodash/delay';
import keyBy from 'lodash/keyBy';
import isEqual from 'lodash/isEqual';
import { useSearchParams } from 'react-router-dom';
import { axios } from '@/services/axios';
import { downloadCSV } from '@/utils/utils';
import {
    CSV_TYPE,
    CSV_TYPE_FX,
    CSV_TYPE_ERROR,
    FIELDS_TO_EXPORT,
    RATE_DNE_ERROR,
    REQUIRED_FIELDS_ERROR,
    INVALID_NUMERIC_ERROR,
    INVALID_ACTIVE_ERROR,
    INVALID_DATA_FILENAME,
    DEFAULT_VERIFICATION_RESULT,
    VERIFICATION_CHECKS_INITIAL,
    SUCCESS_MESSAGE,
    RATE_DELETED,
    RATE_UNCHANGED,
    REQUIRED_CSV_FIELDS,
    NUMERIC_FIELDS,
} from './constants';

const isValidNumber = (val) => {
    if (val === null || val === undefined || val === '') return false;
    const str = String(val).trim();
    if (!isNaN(Number(str))) return true;
    // Allow bracketed numeric patterns, e.g. "3556[6789]"
    return /^[0-9.[\]]+$/.test(str);
};

// Only dynamically-type the actual numeric columns. Papa's blanket
// dynamicTyping otherwise silently converts numeric-looking text fields
// (e.g. Prefix "020") into numbers, stripping leading zeros — which then
// makes every re-uploaded row look "changed" when compared below (#1387).
const NUMERIC_CSV_HEADERS = new Set(
    NUMERIC_FIELDS.map((field) => FIELDS_TO_EXPORT[field]),
);
const shouldDynamicallyType = (header) => NUMERIC_CSV_HEADERS.has(header);

// Rates/costs are compared to a fixed precision to avoid false positives
// from floating-point noise (e.g. 0.1 + 0.2 style rounding artifacts).
const roundForCompare = (val) => Math.round((Number(val) || 0) * 1e6) / 1e6;

// The existing record's isActive may come through as a real boolean, a
// "true"/"false" string, or a number/numeric string depending on the API —
// normalize the representation WITHOUT collapsing distinct values (e.g.
// Active=1 vs Active=2 must stay different), so any actual change — a
// reactivation (0 -> 1) or a value like 1 -> 2 — is still detected.
const normalizeActive = (val) => {
    if (typeof val === 'boolean') return val ? 1 : 0;
    if (typeof val === 'string') {
        const trimmed = val.trim().toLowerCase();
        if (trimmed === 'true') return 1;
        if (trimmed === 'false') return 0;
    }
    const num = Number(val);
    return Number.isNaN(num) ? 0 : num;
};

// Compared to decide whether an uploaded row actually changes an existing
// rate — rows that match are left unmodified instead of being resubmitted
// as an edit (#1387).
const normalizeForCompare = (source) => ({
    country: (source?.country ?? '').toString().trim(),
    name: (source?.name ?? '').toString().trim(),
    prefix: (source?.prefix ?? '').toString().trim(),
    priority: roundForCompare(source?.priority),
    minPrice: roundForCompare(source?.minPrice),
    flagFallInRate: roundForCompare(source?.flagFallInRate),
    flagFallOutRate: roundForCompare(source?.flagFallOutRate),
    inboundRate: roundForCompare(source?.inboundRate),
    outboundRate: roundForCompare(source?.outboundRate),
    isActive: normalizeActive(source?.isActive),
});

const isRateUnchanged = (row, existingRecord) =>
    Boolean(existingRecord) &&
    isEqual(normalizeForCompare(row), normalizeForCompare(existingRecord));

export default function useCSVInternationalRates() {
    const [verificationChecks, setVerificationChecks] = React.useState(
        VERIFICATION_CHECKS_INITIAL.map((c) => ({ ...c })),
    );
    const [uploadData, setUploadData] = React.useState(null);
    const [payloads, setPayloads] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [verificationResult, setVerificationResult] = React.useState(
        DEFAULT_VERIFICATION_RESULT,
    );

    const [searchParams] = useSearchParams();
    const internationalRateListID = searchParams.get('listId');

    const markCheck = (i) => {
        delay(
            (index) => {
                setVerificationChecks((prev) => {
                    const next = [...prev];
                    if (next[index]) {
                        next[index].check = true;
                        if (index < next.length - 1)
                            next[index + 1].current = true;
                    }
                    return next;
                });
            },
            750,
            i,
        );
    };

    const handleReset = () => {
        setVerificationChecks(
            VERIFICATION_CHECKS_INITIAL.map((c) => ({ ...c })),
        );
        setUploadData(null);
        setPayloads([]);
        setLoading(false);
        setError('');
        setVerificationResult(DEFAULT_VERIFICATION_RESULT);
    };

    const handleExportInvalidData = () => {
        const failed = verificationResult.failed.data;
        const rows = failed.map((row) =>
            Object.keys(FIELDS_TO_EXPORT).reduce((acc, key) => {
                const csvHeader = FIELDS_TO_EXPORT[key];
                if (key === 'errors') {
                    acc[csvHeader] = Array.isArray(row[key])
                        ? row[key].join(' | ')
                        : (row[key] ?? '');
                } else if (key === 'isActive') {
                    acc[csvHeader] =
                        row[key] === undefined || row[key] === ''
                            ? ''
                            : row[key]
                              ? 1
                              : 0;
                } else {
                    acc[csvHeader] = row[key] ?? '';
                }
                return acc;
            }, {}),
        );
        const res = Papa.unparse(rows);
        downloadCSV(res, INVALID_DATA_FILENAME);
    };

    const handleUploadData = (e) => {
        const file = e?.target?.files?.[0];
        if (!file) return;

        if (file.type !== CSV_TYPE && file.type !== CSV_TYPE_FX) {
            window.alert(CSV_TYPE_ERROR);
            return;
        }

        setLoading(true);
        setVerificationChecks(
            VERIFICATION_CHECKS_INITIAL.map((c) => ({ ...c })),
        );

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            dynamicTyping: shouldDynamicallyType,
            complete: async (results) => {
                try {
                    setUploadData(results.data);

                    // Step 0: Received CSV
                    markCheck(0);

                    // Step 1: Fetch existing rates for Rate ID validation
                    let existingRatesLookup = {};
                    try {
                        if (internationalRateListID) {
                            const response = await axios.get(
                                `/internationalrates/${internationalRateListID}`,
                            );
                            const existing = Array.isArray(response.data)
                                ? response.data
                                : [];
                            existingRatesLookup = keyBy(existing, 'id');
                        }
                    } catch (_err) {
                        // Continue — any provided Rate IDs will fail validation
                    }

                    markCheck(1);

                    // Step 2: Convert CSV headers to internal field names
                    const csvHeaderToField = Object.entries(
                        FIELDS_TO_EXPORT,
                    ).reduce((acc, [field, csvHeader]) => {
                        acc[csvHeader] = field;
                        return acc;
                    }, {});

                    const converted = results.data
                        .map((row) => {
                            const item = {};
                            Object.entries(csvHeaderToField).forEach(
                                ([csvHeader, field]) => {
                                    const raw = row[csvHeader];
                                    item[field] =
                                        raw === null || raw === undefined
                                            ? ''
                                            : raw;
                                },
                            );
                            return item;
                        })
                        .filter((row) =>
                            // Drop completely empty rows
                            Object.values(row).some(
                                (v) =>
                                    v !== '' && v !== null && v !== undefined,
                            ),
                        );

                    markCheck(2);

                    // Step 3: Verify required fields and Rate ID existence
                    const afterRequiredCheck = converted.map((row) => {
                        const errors = [];

                        const missingFields = REQUIRED_CSV_FIELDS.filter(
                            (field) =>
                                row[field] === '' ||
                                row[field] === null ||
                                row[field] === undefined,
                        );
                        if (missingFields.length > 0) {
                            errors.push(
                                `${REQUIRED_FIELDS_ERROR}: ${missingFields.join(', ')}`,
                            );
                        }

                        // If Rate ID is provided, verify it exists
                        if (row.id && !existingRatesLookup[row.id]) {
                            errors.push(RATE_DNE_ERROR);
                        }

                        // Active must be exactly 0 (delete) or 1 (add/edit)
                        // when provided — anything else (e.g. 2) is invalid.
                        if (row.isActive !== '') {
                            const activeStr = String(row.isActive).trim();
                            if (activeStr !== '0' && activeStr !== '1') {
                                errors.push(INVALID_ACTIVE_ERROR);
                            }
                        }

                        return { ...row, errors };
                    });

                    markCheck(3);

                    // Step 4: Validate numeric fields for rows with no prior errors
                    const afterNumericCheck = afterRequiredCheck.map((row) => {
                        if (row.errors.length > 0) return row;
                        const invalidFields = NUMERIC_FIELDS.filter((field) => {
                            const val = row[field];
                            return (
                                val !== '' &&
                                val !== null &&
                                val !== undefined &&
                                !isValidNumber(val)
                            );
                        });
                        if (invalidFields.length > 0) {
                            return {
                                ...row,
                                errors: [
                                    ...row.errors,
                                    `${INVALID_NUMERIC_ERROR}: ${invalidFields.join(', ')}`,
                                ],
                            };
                        }
                        return row;
                    });

                    markCheck(4);

                    // Partition into passed / failed and build API payloads
                    const passed = [];
                    const failed = [];
                    const builtPayloads = [];

                    afterNumericCheck.forEach((row) => {
                        if (row.errors.length > 0) {
                            failed.push({ ...row });
                            return;
                        }

                        const activeVal = Number(row.isActive);
                        const isNew = !row.id;
                        const isDelete = !isNew && activeVal === 0;
                        const existingRecord = existingRatesLookup[row.id];
                        const isUnchanged =
                            !isNew &&
                            !isDelete &&
                            isRateUnchanged(row, existingRecord);

                        passed.push({
                            ...row,
                            verificationStatus: isDelete
                                ? RATE_DELETED
                                : isUnchanged
                                  ? RATE_UNCHANGED
                                  : SUCCESS_MESSAGE,
                        });

                        // Unchanged rows don't need to be resubmitted (#1387).
                        if (isUnchanged) return;

                        const payload = {
                            country: row.country,
                            name: row.name,
                            prefix: String(row.prefix ?? ''),
                            priority: Number(row.priority),
                            minPrice: Number(row.minPrice),
                            flagFallInRate: Number(row.flagFallInRate),
                            flagFallOutRate: Number(row.flagFallOutRate),
                            inboundRate: Number(row.inboundRate),
                            outboundRate: Number(row.outboundRate),
                            isActive: !isDelete,
                            internationalRateListID,
                            isNewRate: isNew,
                        };

                        if (!isNew) {
                            payload.id = row.id;
                        }

                        builtPayloads.push(payload);
                    });

                    setVerificationResult({
                        passed: { columns: [], data: passed },
                        failed: { columns: [], data: failed },
                    });
                    setPayloads(builtPayloads);
                } catch (err) {
                    setError(
                        err?.response?.data?.message ||
                            err?.message ||
                            'An error occurred during validation.',
                    );
                }
                // Do NOT set loading=false here — loading stays true so the
                // validation-steps view remains visible until the user clicks "View Results".
            },
            error: (err) => {
                setError(err?.message || 'Failed to parse CSV file.');
                setLoading(false);
            },
        });
    };

    return {
        handleUploadData,
        uploadData,
        verificationChecks,
        loading,
        error,
        payloads,
        verificationResult,
        handleReset,
        handleExportInvalidData,
    };
}
